import React, { FC } from 'react';

import { PageProps } from 'gatsby';
import { WindowLocation } from '@gatsbyjs/reach-router';
import { colors } from '../styles/tokens/colors';
import { useSiteMetadata } from '../hooks/useSiteMetadata';
import { useSiteCountry } from '../hooks/useSiteCountry';
import { hreflangSlugMap, alternateWebsites } from '../utils/hreflangMappings';

export interface SeoDataProps {
  readonly metaAuthor?: string;
  readonly metaCopyright?: string;
  readonly h1?: string;
  readonly showOnSitemap: boolean | null;
  readonly remoteTypeName: string;
  readonly priority: number | null;
  readonly pageTitle: string | null;
  readonly pageDescription: string | null;
  readonly canonicalUrl: string | null;
  readonly allowCrawlersToIndex: boolean | null;
  readonly allowCrawlersToFollow: boolean | null;
}

export interface SeoProps {
  title?: string;
  meta?: any[];
  image?: string;
  canonical?: string;
  data?: SeoDataProps | null;
  location?: PageProps['location'] | WindowLocation | null;
}

/*
 * Component to render SEO data into appropriate tags for the <head> tag.
 * Schema.org properties are handled by the "Schema" component (Schema.tsx).
 */
export const SEO: FC<SeoProps> = ({
  data,
  meta = [],
  title,
  image,
  canonical,
  location,
}) => {
  const {
    siteUrl,
    companyName,
    isBrochureware,
    shortName,
    googleVerification,
    pinterestVerification,
    metaTitleFallback,
    twitter,
  } = useSiteMetadata();

  const { isIe } = useSiteCountry();

  const inLanguage = isIe ? 'en-IE' : 'en-GB';

  const hrefLangTags = [];

  if (isBrochureware) {
    // hrefLangTags.push(
    //   <link
    //     key="hreflang-x-default"
    //     rel="alternate"
    //     hrefLang="x-default"
    //     href="https://www.agriapet.co.uk"
    //   />
    // ); // Best practice is to include this only if it links to a "global" location which includes a language selector for the brand.
    if (
      companyName &&
      companyName.toLowerCase().includes('agria') &&
      location?.pathname &&
      alternateWebsites &&
      hreflangSlugMap &&
      hreflangSlugMap[location.pathname]
    ) {
      const slugMappings = hreflangSlugMap[location.pathname];
      hrefLangTags.push(
        ...alternateWebsites
          .filter(
            ({ lang }) => lang?.toLowerCase() !== inLanguage?.toLowerCase()
          )
          .map(({ lang, domain }) => (
            <link
              key={`hreflang-${lang}`}
              rel="alternate"
              hrefLang={lang}
              href={`${domain}${slugMappings[lang]}`}
            />
          ))
      );
    }
    hrefLangTags.push(
      <link
        key={`hreflang-${location?.pathname || ''}`}
        rel="alternate"
        hrefLang={inLanguage}
        href={`${siteUrl}${location?.pathname || ''}`}
      />
    );
  }

  const sameAs: string[] = [];

  if (twitter) {
    sameAs.push(twitter);
  }

  const verification: any[] = [
    {
      name: 'google-site-verification',
      content: googleVerification,
    },
    {
      name: 'p:domain_verify',
      content: pinterestVerification,
    },
  ];

  const metaTitle = title || data?.pageTitle || metaTitleFallback;
  const metaDescription = data?.pageDescription ? data.pageDescription : '';
  const robotsIndex = data?.allowCrawlersToIndex ? 'index' : 'noIndex';
  const robotsFollow = data?.allowCrawlersToFollow ? 'follow' : 'nofollow';
  const brandColor = colors?.brandTwo100 || null;
  const canonicalUrl = data?.canonicalUrl || canonical || null;

  const metaItems = [
    {
      name: `robots`,
      content: `max-snippet:-1, max-image-preview:large, max-video-preview:-1, ${robotsIndex}, ${robotsFollow}`,
    },
    {
      name: `description`,
      content: metaDescription.replace(/<br\s*[/]?>/gi, '\n'),
    },
    {
      name: `author`,
      content: data?.metaAuthor || null,
    },
    {
      name: `copyright`,
      content: data?.metaCopyright || null,
    },
    {
      name: `theme-color`,
      content: brandColor,
    },
    {
      name: `msapplication-TileColor`,
      content: brandColor,
    },
    {
      name: `msapplication-navbutton-color`,
      content: brandColor,
    },
    {
      name: `apple-mobile-web-app-status-bar-style`,
      content: brandColor,
    },
    {
      property: `og:site_name`,
      content: companyName,
    },
    {
      property: `og:title`,
      content: metaTitle,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:locale`,
      content: inLanguage.replace('-', '_'),
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: 'og:image',
      content: image,
    },
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:creator`,
      content: twitter,
    },
    {
      name: `twitter:title`,
      content: data?.h1 || metaTitle,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    },
    {
      name: 'twitter:image',
      content: image,
    },
  ]
    .filter((m) => !!m.content)
    .concat(meta, verification);

  return (
    <>
      <html lang={inLanguage} />
      <title>{metaTitle}</title>
      {hrefLangTags.map((tag) => tag)}
      {metaItems.map((metaItem, index) => (
        <meta
          name={metaItem?.name}
          content={metaItem?.content as string}
          property={metaItem?.property as string}
          key={index}
        />
      ))}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
    </>
  );
};
